// Saving each entry as it is finished, rather than all of them at the end.
//
// One fact shapes every line of this file. `updateApplication` deletes any
// stored entry whose id is absent from the submitted array — see
// findItemsToDelete in api/src/services/applications. So a per-entry save is
// not "send the entry"; it is "send every entry, with this one changed".
// Getting that wrong destroys a driver's history rather than saving it.
//
// One consequence follows: saves are serialised, because two in flight with
// different snapshots means the slower one wins and the faster one's entry
// vanishes.
//
// Removal is immediate. The confirm sheet already names the employer and the
// period that will reopen, so a deferred delete with an undo window was a
// second safety net for an action that already has one — and the floating
// toast it needed sat on top of the page's own buttons.

import { useCallback, useRef, useState } from 'react'

import {
  isBlankExperience,
  toApplicationPayload,
  toFormExperience,
  unsendableEntries,
} from './formBridge'
import type { FormExperience } from './formBridge'
import type { EntryDraft } from './validation'

export interface EmploymentPersistence {
  saveEntry: (draft: EntryDraft, id: string | null) => Promise<void>
  removeEntry: (id: string) => Promise<void>
  saving: boolean
  error: unknown
}

export interface EmploymentPersistenceOptions {
  experiences: FormExperience[]
  onExperiencesChange: (experiences: FormExperience[]) => void
  submit: (payload: ReturnType<typeof toApplicationPayload>) => Promise<unknown>
  /** Generates an id for a new entry, so the server can match it next time. */
  newId: () => string
}

export const useEmploymentPersistence = ({
  experiences,
  onExperiencesChange,
  submit,
  newId,
}: EmploymentPersistenceOptions): EmploymentPersistence => {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // The latest values, so a queued save does not submit a stale snapshot.
  //
  // Resynced only when the parent genuinely hands over a different array. A
  // plain reassignment on every render would throw away the optimistic list
  // whenever this re-rendered before the parent had fed the change back — which
  // it does, because saving sets state here.
  const latest = useRef(experiences)
  const lastFromProps = useRef(experiences)
  if (lastFromProps.current !== experiences) {
    lastFromProps.current = experiences
    latest.current = experiences
  }
  const onChangeRef = useRef(onExperiencesChange)
  onChangeRef.current = onExperiencesChange
  const submitRef = useRef(submit)
  submitRef.current = submit

  // Saves run one at a time. Two in flight with different snapshots means the
  // slower one wins, and whatever the faster one added is gone.
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  const send = useCallback((next: FormExperience[]) => {
    // Refused rather than attempted. startDate is required by the API and a save
    // sends every entry, so one undated row fails the whole mutation — and the
    // failure arrives as a GraphQL error about a row the driver never opened.
    const blocking = unsendableEntries(next)
    if (blocking.length > 0) {
      const names = blocking
        .map((entry) => entry.companyName?.trim())
        .filter(Boolean)
      throw new Error(
        names.length > 0
          ? `Add dates to ${names.join(' and ')} before saving.`
          : 'One entry still needs its dates before anything can be saved.'
      )
    }

    const run = queue.current
      .catch(() => undefined)
      .then(() => submitRef.current(toApplicationPayload(next)))

    queue.current = run
    return run
  }, [])

  const saveEntry = useCallback(
    async (draft: EntryDraft, id: string | null) => {
      const current = latest.current
      const index = current.findIndex((e) => e.employmentExperienceId === id)

      const next =
        id != null && index >= 0
          ? current.map((experience, position) =>
              position === index
                ? toFormExperience(draft, experience)
                : experience
            )
          : [
              ...current.filter((experience) => !isBlankExperience(experience)),
              {
                ...toFormExperience(draft),
                employmentExperienceId: newId(),
              },
            ]

      latest.current = next
      onChangeRef.current(next)

      setSaving(true)
      setError(null)
      try {
        await send(next)
      } catch (caught) {
        setError(caught)
      } finally {
        setSaving(false)
      }
    },
    [newId, send]
  )

  const removeEntry = useCallback(
    async (id: string) => {
      const next = latest.current.filter(
        (experience) => experience.employmentExperienceId !== id
      )
      latest.current = next
      onChangeRef.current(next)

      setSaving(true)
      try {
        // Every remaining entry goes up; the API deletes what is absent, which
        // is exactly how this one gets removed.
        await send(next)
      } catch (caught) {
        setError(caught)
      } finally {
        setSaving(false)
      }
    },
    [send]
  )

  return { saveEntry, removeEntry, saving, error }
}
